import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

import AuthContext from '../../context/authContext';
import useIssue from '../../hooks/useIssue';
import useBranch from '../../hooks/useBranch';
import {
  ISSUE_PRIORITIES,
  ISSUE_PRIORITY_BY_KEY,
  ESCALATION_RUN_LABELS,
  formatDurationMs,
} from '../../util/issueConstants';
import {
  getRechartsTooltipContentStyle,
  getCartesianGridStroke,
  getRechartsAxisStroke,
  getStatisticsKpiLabelColor,
} from '../../util/chartTheme';

// Series palettes validated for CVD safety and surface contrast per mode
// (see dataviz validation). Fixed order: [created/reaction, resolved/fix, extra].
const SERIES_COLORS = {
  light: ['#1976d2', '#ef6c00', '#7b1fa2'],
  dark: ['#2196f3', '#e65100', '#ba68c8'],
};

const KpiTile = ({ label, value, helper, accent }) => {
  const theme = useTheme();
  return (
    <Paper variant='outlined' sx={{ p: 2, height: '100%' }}>
      <Typography variant='caption' sx={{ color: getStatisticsKpiLabelColor(theme) }}>
        {label}
      </Typography>
      <Typography variant='h4' sx={{ fontWeight: 700, color: accent || 'text.primary', my: 0.5 }}>
        {value}
      </Typography>
      {helper && (
        <Typography variant='caption' color='text.secondary'>
          {helper}
        </Typography>
      )}
    </Paper>
  );
};

// IT statistics: reaction times, fix durations, volumes, downtime and
// escalation outcomes based on the issue/outage tracking.
const ItStatisticsView = () => {
  const theme = useTheme();
  const authCtx = useContext(AuthContext);
  const { getStatistics } = useIssue();
  const { getBranches } = useBranch();

  const [stats, setStats] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [from, setFrom] = useState(dayjs().subtract(90, 'day'));
  const [to, setTo] = useState(dayjs());
  const [loading, setLoading] = useState(true);

  const seriesColors = SERIES_COLORS[theme.palette.mode === 'dark' ? 'dark' : 'light'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getStatistics({
        from: from ? from.format('YYYY-MM-DD') : undefined,
        to: to ? to.format('YYYY-MM-DD') : undefined,
        branchId: branchId || undefined,
      });
      if (response) setStats(response);
    } catch {
      // toast handled in hook
    } finally {
      setLoading(false);
    }
  }, [getStatistics, from, to, branchId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getBranches()
      .then(response => setBranches(response?.branches || response || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canRead = authCtx.checkRight('issue:statistics') || authCtx.checkRight('issue');

  const weeklyData = useMemo(
    () =>
      (stats?.weeklySeries || []).map(entry => ({
        week: `KW ${entry._id.week}`,
        Erstellt: entry.created,
        Gelöst: entry.resolved,
        P1: entry.p1,
      })),
    [stats]
  );

  const priorityData = useMemo(
    () =>
      ISSUE_PRIORITIES.map(priority => {
        const row = (stats?.byPriority || []).find(entry => entry._id === priority.key);
        return {
          priority: priority.shortLabel,
          Reaktionszeit: row?.avgReactionMs ? Math.round(row.avgReactionMs / 60000) : 0,
          Lösungszeit: row?.avgResolutionMs ? Math.round(row.avgResolutionMs / 60000) : 0,
          total: row?.total || 0,
        };
      }),
    [stats]
  );

  const downtimeData = useMemo(
    () =>
      (stats?.outageStats || []).slice(0, 10).map(entry => ({
        name: entry.resourceName ? `${entry.resourceName}` : entry.entertainmentName || 'Unbekannt',
        Stunden: Math.round(((entry.totalDowntimeMs || 0) / 3600000) * 10) / 10,
        outages: entry.outages,
      })),
    [stats]
  );

  const kpis = useMemo(() => {
    if (!stats) return null;
    const openTotal = (stats.openSnapshot || []).reduce((sum, row) => sum + row.count, 0);
    const openP1 = (stats.openSnapshot || []).find(row => row._id === 'p1')?.count || 0;
    const p1Row = (stats.byPriority || []).find(row => row._id === 'p1');
    const allReaction = (stats.byPriority || []).filter(row => row.avgReactionMs);
    const avgReaction =
      allReaction.length > 0
        ? allReaction.reduce((sum, row) => sum + row.avgReactionMs, 0) / allReaction.length
        : null;
    const allResolution = (stats.byPriority || []).filter(row => row.avgResolutionMs);
    const avgResolution =
      allResolution.length > 0
        ? allResolution.reduce((sum, row) => sum + row.avgResolutionMs, 0) / allResolution.length
        : null;
    const downtimeTotal = (stats.outageStats || []).reduce(
      (sum, row) => sum + (row.totalDowntimeMs || 0),
      0
    );
    const exhausted = (stats.escalationStats || []).find(row => row._id === 'exhausted');
    return { openTotal, openP1, p1Row, avgReaction, avgResolution, downtimeTotal, exhausted };
  }, [stats]);

  if (!canRead) return <Navigate to='/' replace />;

  const tooltipStyle = getRechartsTooltipContentStyle(theme);
  const gridStroke = getCartesianGridStroke(theme);
  const axisStroke = getRechartsAxisStroke(theme);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant='h5' sx={{ fontWeight: 700, flexGrow: 1 }}>
          IT-Statistik
        </Typography>
        <DatePicker
          label='Von'
          value={from}
          onChange={setFrom}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
        />
        <DatePicker
          label='Bis'
          value={to}
          onChange={setTo}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
        />
        <Select
          size='small'
          value={branchId}
          onChange={event => setBranchId(event.target.value)}
          displayEmpty
          sx={{ minWidth: 180 }}
        >
          <MenuItem value=''>Alle Standorte</MenuItem>
          {branches.map(branch => (
            <MenuItem key={branch._id} value={branch._id}>
              {branch.locationName || branch.longName}
            </MenuItem>
          ))}
        </Select>
        <Button variant='contained' startIcon={<RefreshIcon />} onClick={load}>
          Anwenden
        </Button>
      </Box>

      {loading || !stats ? (
        <Grid container spacing={2}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Skeleton variant='rounded' height={120} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={6} md={2.4}>
              <KpiTile
                label='Offene Issues'
                value={kpis.openTotal}
                helper={`davon ${kpis.openP1} × P1`}
                accent={kpis.openP1 > 0 ? ISSUE_PRIORITY_BY_KEY.p1.color : undefined}
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <KpiTile
                label='Ø Reaktionszeit P1'
                value={kpis.p1Row?.avgReactionMs ? formatDurationMs(kpis.p1Row.avgReactionMs) : '–'}
                helper='Meldung bis erste Reaktion'
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <KpiTile
                label='Ø Reaktionszeit gesamt'
                value={kpis.avgReaction ? formatDurationMs(kpis.avgReaction) : '–'}
                helper='alle Prioritäten'
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <KpiTile
                label='Ø Lösungszeit (MTTR)'
                value={kpis.avgResolution ? formatDurationMs(kpis.avgResolution) : '–'}
                helper='Meldung bis erledigt'
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <KpiTile
                label='Downtime gesamt'
                value={formatDurationMs(kpis.downtimeTotal)}
                helper={`${(stats.outageStats || []).reduce((sum, row) => sum + row.outages, 0)} Ausfälle im Zeitraum`}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Paper variant='outlined' sx={{ p: 2 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                  Issues pro Kalenderwoche
                </Typography>
                <ResponsiveContainer width='100%' height={280}>
                  <LineChart data={weeklyData}>
                    <CartesianGrid stroke={gridStroke} vertical={false} />
                    <XAxis dataKey='week' stroke={axisStroke} tick={{ fontSize: 12 }} />
                    <YAxis stroke={axisStroke} allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend />
                    <Line
                      type='monotone'
                      dataKey='Erstellt'
                      stroke={seriesColors[0]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      type='monotone'
                      dataKey='Gelöst'
                      stroke={seriesColors[1]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      type='monotone'
                      dataKey='P1'
                      stroke={seriesColors[2]}
                      strokeWidth={2}
                      strokeDasharray='6 3'
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper variant='outlined' sx={{ p: 2 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                  Ø Zeiten nach Priorität (Minuten)
                </Typography>
                <ResponsiveContainer width='100%' height={280}>
                  <BarChart data={priorityData}>
                    <CartesianGrid stroke={gridStroke} vertical={false} />
                    <XAxis dataKey='priority' stroke={axisStroke} tick={{ fontSize: 12 }} />
                    <YAxis stroke={axisStroke} allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend />
                    <Bar dataKey='Reaktionszeit' fill={seriesColors[0]} radius={[4, 4, 0, 0]} />
                    <Bar dataKey='Lösungszeit' fill={seriesColors[1]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={7}>
              <Paper variant='outlined' sx={{ p: 2 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                  Downtime nach Raum / Entertainment (Stunden)
                </Typography>
                {downtimeData.length === 0 ? (
                  <Typography variant='body2' color='text.secondary' sx={{ py: 4 }}>
                    Keine Ausfälle im gewählten Zeitraum.
                  </Typography>
                ) : (
                  <ResponsiveContainer
                    width='100%'
                    height={Math.max(180, downtimeData.length * 36)}
                  >
                    <BarChart data={downtimeData} layout='vertical'>
                      <CartesianGrid stroke={gridStroke} horizontal={false} />
                      <XAxis type='number' stroke={axisStroke} tick={{ fontSize: 12 }} />
                      <YAxis
                        type='category'
                        dataKey='name'
                        stroke={axisStroke}
                        width={160}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey='Stunden' fill={seriesColors[0]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper variant='outlined' sx={{ p: 2, height: '100%' }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                  Eskalationen
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {(stats.escalationStats || []).map(row => (
                    <Chip
                      key={row._id}
                      label={`${ESCALATION_RUN_LABELS[row._id] || row._id}: ${row.count}`}
                      color={
                        row._id === 'acknowledged'
                          ? 'success'
                          : row._id === 'exhausted'
                            ? 'error'
                            : 'default'
                      }
                      variant='outlined'
                    />
                  ))}
                  {(stats.escalationStats || []).length === 0 && (
                    <Typography variant='body2' color='text.secondary'>
                      Keine Eskalationen im Zeitraum.
                    </Typography>
                  )}
                </Box>
                {(stats.escalationStats || [])
                  .filter(row => row._id === 'acknowledged')
                  .map(row => (
                    <Box key={row._id}>
                      <Typography variant='body2' color='text.secondary'>
                        Ø Zeit bis Reaktion:{' '}
                        <strong>
                          {row.avgTimeToAckMs ? formatDurationMs(row.avgTimeToAckMs) : '–'}
                        </strong>
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Ø SMS bis Reaktion:{' '}
                        <strong>{Math.round((row.avgSmsSent || 0) * 10) / 10}</strong>
                      </Typography>
                    </Box>
                  ))}
              </Paper>
            </Grid>

          </Grid>
        </>
      )}
    </Box>
  );
};

export default ItStatisticsView;
